import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Loader2 } from 'lucide-react';
import type { TaskSuggestion } from './TaskSuggestionCard';

type Priority = 'critical' | 'high' | 'medium' | 'low';

const PRIORITY_CONFIG: Record<Priority, { label: string; badgeStyle: string }> = {
  critical: {
    label: 'Critical',
    badgeStyle: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  high: {
    label: 'High',
    badgeStyle: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  medium: {
    label: 'Medium',
    badgeStyle: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  low: {
    label: 'Low',
    badgeStyle: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  },
};

interface TaskDetailPanelProps {
  suggestion: TaskSuggestion | null;
  isRunning: boolean;
  onClose: () => void;
  onRun: (taskClass: string) => void;
}

export function TaskDetailPanel({ suggestion, isRunning, onClose, onRun }: TaskDetailPanelProps) {
  useEffect(() => {
    if (!suggestion) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [suggestion, onClose]);

  const priority = (suggestion?.priority || 'medium') as Priority;
  const config = PRIORITY_CONFIG[priority];

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/20 dark:bg-black/40 z-40 transition-opacity duration-200 ${suggestion ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 z-50 flex flex-col shadow-xl transition-transform duration-300 ease-in-out ${suggestion ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {suggestion && (
          <>
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div className="flex flex-col gap-1.5 min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white leading-snug">
                  {suggestion.displayName}
                </h2>
                <span
                  className={`self-start px-2 py-0.5 text-xs font-medium rounded-full ${config.badgeStyle}`}
                >
                  {config.label}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mt-0.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 [&_br]:block">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="mb-2">{children}</p>,
                  }}
                >
                  {suggestion.reason}
                </ReactMarkdown>
              </div>
            </div>

            <div className="shrink-0 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => onRun(suggestion.taskClass)}
                disabled={isRunning}
                className="w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white"
              >
                {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isRunning ? 'Starting...' : priority === 'critical' ? 'Fix Now' : 'Run'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
